import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { KeyRound, ArrowLeft, User, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';

const ForgotPassword = () => {
  const [identifier, setIdentifier] = useState('');
  const [success, setSuccess] = useState(false);

  const forgotPasswordMutation = useMutation(
    async (data) => {
      const response = await api.post('/auth/forgot-password', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSuccess(true);
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reset password');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!identifier.trim()) {
      toast.error('Please enter your username or email');
      return;
    }

    forgotPasswordMutation.mutate({ identifier: identifier.trim() });
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          {/* Success Message */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                Password Reset Sent!
              </h2>
              <p className="text-secondary-600 mb-6">
                A temporary password has been sent to your registered mobile number via SMS.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-900 font-medium mb-2">Next Steps:</p>
                <ol className="text-sm text-blue-700 text-left space-y-1">
                  <li>1. Check your SMS for the temporary password</li>
                  <li>2. Login with your username and the temporary password</li>
                  <li>3. You'll be required to change your password immediately</li>
                </ol>
              </div>
              <Link
                to="/login"
                className="btn btn-primary w-full justify-center"
              >
                Go to Login
              </Link>
            </div>
          </div>

          {/* Back to Forgot Password */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setSuccess(false);
                setIdentifier('');
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Reset another password
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <KeyRound className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Forgot Password?
          </h1>
          <p className="text-secondary-600">
            Enter your username or email and we'll send a temporary password to your registered mobile number.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <form onSubmit={handleSubmit} className="px-6 py-6">
            <div className="space-y-4">
              {/* Username or Email Field */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Username or Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    type="text"
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your username or email"
                    disabled={forgotPasswordMutation.isLoading}
                  />
                </div>
                <p className="text-xs text-secondary-500 mt-1">
                  Enter either your username or registered email address
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <KeyRound className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">How it works</p>
                    <ul className="text-xs text-blue-700 mt-1 space-y-1">
                      <li>• A new temporary 12-character password will be generated</li>
                      <li>• It will be sent to your registered mobile number via SMS</li>
                      <li>• You'll be required to change it on first login</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={forgotPasswordMutation.isLoading}
                  className="w-full btn btn-primary justify-center"
                >
                  {forgotPasswordMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4 mr-2" />
                      Reset Password
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </Link>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-secondary-500">
            If you don't have a mobile number registered or encounter any issues, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
